import React from "react"
import * as Yup from "yup"
import {Formik} from "formik"
import Dropdown from "../../../Address/Dropdown"
import TextField from "../../../Address/Textfield"
import Card from "../../../../containers/Card/Card"
import DateField from "../../../DateField/DateField"
import AddressComponent from "../../../Address/Address"
import {countries} from "../../../../resources/countries"
import {US_COUNTRY_CODE} from "../../../../resources/address"
import {Address, FullName, Phone} from "../../../../resources/common"
import LeftRightSlots from "../../../../containers/LeftRightSlots/LeftRightSlots"
import beneficialOwnerValidationSchema from "./reevaluateBeneficialOwnerValidationSchema"
import IdentificationTypeSelect from "../../../IdentificationTypeSelect/ApplicationIdentificationNumber"
import PhoneNumber from "../../../Address/PhoneNumber"
import {ApplicationResubmitForms} from "../../../../pages/ReevaluateApplication/ReevaluatePage"
import Textfield from "../../../Address/Textfield"
import {AnnualIncome, Occupation, SourceOfIncome} from "../../../../resources/application"

export interface ReevaluatedBeneficialOwnerApplicationData {
	fullName: FullName
	address: Address
	dateOfBirth: Date | string
	email: string
	phone: Phone
	ssn?: string
	nationality?: string
	passport?: string
	matriculaConsular?: string
	percentage?: number | ""
	occupation?: Occupation | ""
	sourceOfIncome?: SourceOfIncome | ""
	annualIncome?: AnnualIncome | ""
}

export default function ReevaluateBeneficialOwnerForm(props: {
	beneficialOwner: ReevaluatedBeneficialOwnerApplicationData
	submitCallback: (values: ReevaluatedBeneficialOwnerApplicationData) => void
	formId: ApplicationResubmitForms
	disableSensitiveFields?: boolean
}) {
	const {submitCallback, beneficialOwner, formId, disableSensitiveFields = false} = props

	const validationSchema = Yup.object().shape(beneficialOwnerValidationSchema)

	const initialFormValues = {
		fullName: {
			first: beneficialOwner.fullName.first,
			last: beneficialOwner.fullName.last,
		},
		address: {
			street: beneficialOwner.address?.street,
			street2: beneficialOwner.address?.street2,
			city: beneficialOwner.address?.city,
			state: beneficialOwner.address?.state ?? "",
			postalCode: beneficialOwner.address?.postalCode,
			country: beneficialOwner.address?.country ?? US_COUNTRY_CODE,
		},
		ssn: beneficialOwner.ssn ?? "",
		nationality: beneficialOwner.nationality ?? "US",
		passport: beneficialOwner.passport ?? "",
		matriculaConsular: beneficialOwner.matriculaConsular ?? "",
		dateOfBirth: beneficialOwner.dateOfBirth,
		identificationType: beneficialOwner.ssn
			? "ssn"
			: beneficialOwner.matriculaConsular
			? "matriculaConsular"
			: "passport",
		phone: beneficialOwner.phone,
		email: beneficialOwner.email,
		percentage: beneficialOwner.percentage,
		occupation: beneficialOwner.occupation,
		sourceOfIncome: beneficialOwner.sourceOfIncome,
		annualIncome: beneficialOwner.annualIncome,
	}

	const title = `Beneficial Owner - ${beneficialOwner.fullName.first} ${beneficialOwner.fullName.last}`

	return (
		<Card className="reevaluate-form" title={title}>
			<Formik
				initialValues={initialFormValues}
				validationSchema={validationSchema}
				onSubmit={(values) =>
					submitCallback({
						...values,
						percentage: values.percentage === "" ? undefined : values.percentage,
						occupation: values.occupation === "" ? undefined : values.occupation,
						annualIncome: values.annualIncome === "" ? undefined : values.annualIncome,
						sourceOfIncome: values.sourceOfIncome === "" ? undefined : values.sourceOfIncome,
					})
				}
			>
				{({values, handleSubmit, handleChange, setFieldValue, handleBlur}) => {
					return (
						<form noValidate onSubmit={handleSubmit} id={formId}>
							<LeftRightSlots
								left={
									<>
										<TextField
											label="First Name"
											placeholder="Enter first name"
											name="fullName.first"
											value={values.fullName.first}
											onChange={handleChange}
											onBlur={handleBlur}
											disabled={disableSensitiveFields}
										/>
										<TextField
											label="Last Name"
											placeholder="Enter last name"
											name="fullName.last"
											value={values.fullName.last}
											onChange={handleChange}
											onBlur={handleBlur}
											disabled={disableSensitiveFields}
										/>

										<IdentificationTypeSelect
											value={values.identificationType}
											onChange={handleChange}
											label="Document Type"
											disabled={disableSensitiveFields}
										/>

										{values.identificationType === "matriculaConsular" && (
											<TextField
												label="Document ID #"
												placeholder="Enter Matricula Consular"
												name="matriculaConsular"
												value={values.matriculaConsular}
												onChange={(value) => {
													handleChange(value)
													setFieldValue("ssn", "")
													setFieldValue("passport", "")
												}}
												onBlur={handleBlur}
												disabled={disableSensitiveFields}
											/>
										)}

										{values.identificationType === "ssn" && (
											<>
												<Dropdown
													name="nationality"
													label="Nationality"
													value={values.nationality}
													onChange={handleChange}
													onBlur={handleBlur}
													disabled={disableSensitiveFields}
												>
													{Object.keys(countries).map((countryCode) => (
														<option key={countryCode} value={countryCode}>
															{countries[countryCode]}
														</option>
													))}
												</Dropdown>
												<TextField
													label="Document ID #"
													placeholder="Enter SSN"
													name="ssn"
													value={values.ssn}
													onChange={(value) => {
														handleChange(value)
														setFieldValue("matriculaConsular", "")
														setFieldValue("passport", "")
													}}
													onBlur={handleBlur}
													disabled={disableSensitiveFields}
												/>
											</>
										)}

										{values.identificationType === "passport" ? (
											<>
												<Dropdown
													name="nationality"
													label="Nationality"
													value={values.nationality}
													onChange={handleChange}
													onBlur={handleBlur}
													disabled={disableSensitiveFields}
												>
													{Object.keys(countries).map((countryCode) => (
														<option key={countryCode} value={countryCode}>
															{countries[countryCode]}
														</option>
													))}
												</Dropdown>

												<TextField
													label="Document ID #"
													placeholder="Enter Passport"
													name="passport"
													value={values.passport}
													onChange={(value) => {
														handleChange(value)
														setFieldValue("matriculaConsular", "")
														setFieldValue("ssn", "")
													}}
													onBlur={handleBlur}
													disabled={disableSensitiveFields}
												/>
											</>
										) : null}

										<DateField
											name="dateOfBirth"
											label="Birth Date"
											value={values.dateOfBirth as string}
											onChange={handleChange}
											onBlur={handleBlur}
											disabled={disableSensitiveFields}
										/>
										<TextField
											label="Email"
											placeholder="Enter email"
											name="email"
											value={values.email}
											onChange={(value) => {
												handleChange(value || undefined)
											}}
											onBlur={handleBlur}
										/>

										<Dropdown
											name="occupation"
											label="Occupation"
											value={values.occupation}
											onChange={handleChange}
											onBlur={handleBlur}
										>
											{beneficialOwner.occupation ? <React.Fragment /> : <option value="" />}
											{Object.entries(Occupation).map(([key, value]) => (
												<option key={key} value={key}>
													{value}
												</option>
											))}
										</Dropdown>

										<Dropdown
											name="annualIncome"
											label="Annual Income"
											value={values.annualIncome}
											onChange={handleChange}
											onBlur={handleBlur}
										>
											{beneficialOwner.annualIncome ? <React.Fragment /> : <option value="" />}
											{Object.entries(AnnualIncome).map(([key, value]) => (
												<option key={key} value={key}>
													{value}
												</option>
											))}
										</Dropdown>

										<Dropdown
											name="sourceOfIncome"
											label="Source Of Income"
											value={values.sourceOfIncome}
											onChange={handleChange}
											onBlur={handleBlur}
										>
											{beneficialOwner.sourceOfIncome ? <React.Fragment /> : <option value="" />}
											{Object.entries(SourceOfIncome).map(([key, value]) => (
												<option key={key} value={key}>
													{value}
												</option>
											))}
										</Dropdown>

										<PhoneNumber
											fieldClassName={"reevaluate-phone-number-form"}
											name="phoneNumber"
											label="Phone Number"
											countryCodeName="phone.countryCode"
											phoneNumberName="phone.number"
											countryCodeValue={values.phone.countryCode}
											phoneNumberValue={values.phone?.number}
											countryCodeOnChange={(value) => {
												setFieldValue("phone.countryCode", value)
											}}
											phoneNumberOnChange={(value) => {
												setFieldValue("phone.number", value)
											}}
											countryCodeOnBlur={handleBlur}
											phoneNumberOnBlur={handleBlur}
											infoTooltipText="Enter the country code followed by the phone number."
										/>

										<div className={"percentage"}>
											<Textfield
												label="Percentage"
												placeholder="Enter Percentage"
												name="percentage"
												value={values.percentage ?? ""}
												onChange={handleChange}
												onBlur={handleBlur}
												type="number"
												min={25}
												max={100}
											/>
										</div>
									</>
								}
								right={
									<AddressComponent
										closed={false}
										namePrefix="address."
										street={values.address.street}
										street2={values.address.street2}
										city={values.address.city}
										country={values.address.country}
										state={values.address.state}
										postalCode={values.address.postalCode}
										isUsOnlyAddress={false}
										handleChange={handleChange}
										handleBlur={handleBlur}
										setFieldValue={setFieldValue}
									/>
								}
							/>
						</form>
					)
				}}
			</Formik>
		</Card>
	)
}
